import { Link } from 'react-router-dom';
import { Check, ArrowRight, AlertCircle, MessageCircle } from 'lucide-react';

const Pricing = () => {

  // Premium features list
  const premiumFeatures = [
    {
      title: "Transferts Bancaires",
      description: "Recevez les paiements directement sur votre compte bancaire",
      icon: <ArrowRight className="w-5 h-5" />,
      price: "Frais par transaction"
    },
    {
      title: "Retraits en Espèces",
      description: "Service de retrait pour les paiements reçus",
      icon: <ArrowRight className="w-5 h-5" />,
      price: "Selon montant"
    },
    {
      title: "Pack Visibilité",
      description: "Atteignez des prospects de qualité pour vos biens en location et autres services",
      icon: <ArrowRight className="w-5 h-5" />,
      price: "À partir de 50 000 FCFA/mois"
    }
  ];

  // Included features
  const includedFeatures = [
    "Nombre illimité de maisons et locataires",
    "Nombre illimité de collaborateurs",
    "Gestion complète des biens et locataires",
    "Encaissement par mobile money (sans frais)",
    "Génération automatique de quittances",
    "Facture normalisée",
    "Tableau de bord en temps réel",
    "Notifications de paiement automatiques",
    "Suivi des retards et impayés",
    "Support pendant les heures de bureau",
    "Mises à jour gratuites et continues"
  ];

  return (
    <div id="pricing" className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Tarification Simple et Transparente
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Un tarif unique et prévisible pour gérer votre portefeuille immobilier en toute sérénité
          </p>
        </div>

        {/* Main Free Tier Card */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-1">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Content section */}
              <div className="p-8 md:p-12">
                <div className="bg-blue-600 text-white inline-block px-4 py-1 rounded-full text-sm font-medium mb-4">
                  Tarif Unique
                </div>
                <div className="mb-4">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">
                    1000 FCFA par propriété
                  </h3>
                  <p className="text-lg text-blue-600 font-semibold">
                    Minimum 15 000 FCFA/mois
                  </p>
                </div>
                <p className="text-gray-600 mb-8">
                  Accédez à toutes les fonctionnalités complètes de Recouvro.
                  Aucune limite, aucun frais caché.
                </p>

                <div className="mb-8 space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <div className="bg-blue-100 p-2 rounded-full mr-4 flex-shrink-0">
                        <AlertCircle className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Comment ça marche ?</h4>
                        <p className="text-gray-600 text-sm mb-1">
                          • 1-15 propriétés = 15 000 FCFA/mois (tarif minimum)
                        </p>
                        <p className="text-gray-600 text-sm">
                          • 16+ propriétés = 1000 FCFA × nombre de propriétés
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <div className="bg-green-100 p-2 rounded-full mr-4 flex-shrink-0">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Bonus de Récupération</h4>
                        <p className="text-gray-600 text-sm">
                          Nous percevons 10% sur les pénalités de retard récupérées
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/register"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 hover:opacity-90 transition-all shadow-md hover:shadow-lg"
                >
                  Commencer maintenant
                </Link>
              </div>
              
              {/* Features list */}
              <div className="bg-gray-50 p-8 md:p-12">
                <h4 className="text-xl font-semibold mb-4">Fonctionnalités incluses:</h4>
                <ul className="space-y-4">
                  {includedFeatures.map((feature, index) => (
                    <li key={index} className="flex">
                      <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Features Section */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Services Additionnels
            </h3>
            <p className="text-gray-600">
              Des options supplémentaires pour étendre vos possibilités
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {premiumFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h4 className="text-lg font-semibold mb-2">{feature.title}</h4>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="mt-auto">
                  <span className="text-blue-600 font-medium">{feature.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial/Promise Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl text-white p-8 md:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Notre Engagement
            </h3>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              Une tarification transparente et prévisible pour vous permettre de gérer votre activité en toute sérénité.
              Nous réussissons ensemble lorsque vous récupérez vos paiements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white bg-opacity-10 rounded-xl p-6">
              <h4 className="font-semibold mb-2">Tarif Transparent</h4>
              <p className="opacity-90">Pas de frais cachés, un tarif simple basé sur votre portefeuille</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-xl p-6">
              <h4 className="font-semibold mb-2">Développement continu</h4>
              <p className="opacity-90">Des améliorations régulières incluses dans votre abonnement</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-xl p-6">
              <h4 className="font-semibold mb-2">Support dédié</h4>
              <p className="opacity-90">Une équipe disponible pour vous accompagner à chaque étape</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-white border border-gray-200 rounded-lg p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Prêt à simplifier votre gestion immobilière ?
            </h3>
            <p className="text-gray-600 mb-6">
              Rejoignez les nombreuses agences qui font confiance à Recouvro pour gérer leurs locations plus efficacement.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 hover:opacity-90 transition-all shadow-md hover:shadow-lg"
            >
              Commencer maintenant
            </Link>
          </div>
        </div>
      </div>

      {/* WhatsApp Widget */}
      <a
        href="https://wa.me/2290197826458?text=Bonjour,%20je%20suis%20intéressé%20par%20Recouvro"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-all hover:scale-110 z-50 flex items-center justify-center group"
        aria-label="Contactez-nous sur WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute right-full mr-3 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Contactez-nous
        </span>
      </a>

      <style jsx>{`
        @keyframes draw {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes circleFadeIn {
          to {
            stroke-dashoffset: 0;
            opacity: 1;
          }
        }

        .success-checkmark circle {
          stroke-dasharray: 151;
          stroke-dashoffset: 151;
          animation: circleFadeIn 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
        }

        .success-checkmark path {
          stroke-dasharray: 48;
          stroke-dashoffset: 48;
          animation: draw 0.6s 0.4s cubic-bezier(0.65, 0, 0.45, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default Pricing;